import type { Channel } from '@/types/api';

export interface ChannelReorderOrder {
  id: string;
  position: number;
  parentId?: string | null;
}

const byPositionAsc = (a: Channel, b: Channel) => a.position - b.position;

/**
 * Compute new channel positions after a drag-and-drop reorder, matching the
 * server's `/channels/reorder` contract (`{ id, position, parentId? }[]`).
 *
 * Two scopes:
 *   - **Categories** reorder only among themselves (a category dropped on a
 *     category). Positions are renumbered 0..n across all categories.
 *   - **Channels** (TEXT / VOICE) reorder within a parent scope, where the
 *     scope is the *target's* parent. Dropping a channel onto a channel in a
 *     different category therefore re-parents the dragged channel into that
 *     category. Only the affected scope is renumbered; the source scope keeps
 *     its order (a positional gap is harmless since rows sort by position).
 *
 * Returns `null` for no-ops / invalid combinations (same id, unknown ids,
 * category dropped on a normal channel or vice-versa).
 */
export function computeChannelReorder(
  channels: Channel[],
  draggingId: string,
  targetId: string,
  pos: 'before' | 'after',
): ChannelReorderOrder[] | null {
  if (draggingId === targetId) return null;
  const byId = new Map(channels.map((c) => [c.id, c]));
  const dragged = byId.get(draggingId);
  const target = byId.get(targetId);
  if (!dragged || !target) return null;

  // Category ↔ category reorder.
  if (dragged.type === 'CATEGORY') {
    if (target.type !== 'CATEGORY') return null;
    const cats = channels.filter((c) => c.type === 'CATEGORY').sort(byPositionAsc);
    const without = cats.filter((c) => c.id !== draggingId);
    const ti = without.findIndex((c) => c.id === targetId);
    if (ti < 0) return null;
    without.splice(pos === 'before' ? ti : ti + 1, 0, dragged);
    return without.map((c, i) => ({ id: c.id, position: i }));
  }

  // Channel reorder: dropping onto another non-category channel. The target's
  // parent defines the destination scope (enabling cross-category moves).
  if (target.type === 'CATEGORY') return null;
  const scopeParent = target.parentId ?? null;
  const scope = channels
    .filter(
      (c) => c.type !== 'CATEGORY' && (c.parentId ?? null) === scopeParent && c.id !== draggingId,
    )
    .sort(byPositionAsc);
  const ti = scope.findIndex((c) => c.id === targetId);
  if (ti < 0) return null;
  scope.splice(pos === 'before' ? ti : ti + 1, 0, dragged);
  return scope.map((c, i) => ({ id: c.id, position: i, parentId: scopeParent }));
}
